/**
 * Dynamic CompactFilters Component
 * 
 * A reusable filter component that can be configured with different filter types and data sources.
 * 
 * Usage Examples:
 * 
 * 1. Legacy Mode (backward compatibility):
 * <CompactFilters
 *   selectedOrganisations={orgs}
 *   selectedDepartments={depts}
 *   selectedGroups={groups}
 *   selectedTemplateTypes={types}
 *   onLegacyFilterChange={(orgs, depts, groups, types) => { ... }}
 * />
 * 
 * 2. Dynamic Mode:
 * <CompactFilters
 *   filters={[
 *     { key: 'categories', label: 'Categories', placeholder: 'Select categories', dataSource: 'props', data: categoryOptions },
 *     { key: 'status', label: 'Status', placeholder: 'Select status', dataSource: 'api', apiEndpoint: 'GetStatusData' },
 *     { key: 'priority', label: 'Priority', placeholder: 'Select priority', dataSource: 'props', data: priorityOptions }
 *   ]}
 *   selectedFilters={{ categories: [], status: [], priority: [] }}
 *   onFilterChange={(filters) => { ... }}
 * />
 */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import styles from './CompactFilters.module.css';
import { Category } from '../../Utils/SelectionPane/SelectionPane';
import { useFilterOptions } from './useFilterOptions';
import type { ISelectOption, FilterConfig } from './filterTypes';
import { dateRangeValue, makeDateRangeOption } from './filterUtils';

// Re-exported so the existing importers of these types keep working.
export type { ISelectOption, FilterConfig, FilterKind } from './filterTypes';

type CompactFiltersProps = {
  filters?: FilterConfig[];
  selectedFilters?: Record<string, ISelectOption[]>;
  onFilterChange?: (filters: Record<string, ISelectOption[]>) => void;
  className?: string;
  categories?: Category<string>[];
  // Legacy props for backward compatibility
  selectedOrganisations?: ISelectOption[];
  selectedDepartments?: ISelectOption[];
  selectedGroups?: ISelectOption[];
  selectedTemplateTypes?: ISelectOption[];
  selectedRoles?: ISelectOption[];
  // Legacy callback for backward compatibility
  onLegacyFilterChange?: (
    organisations: ISelectOption[],
    departments: ISelectOption[],
    groups: ISelectOption[],
    templateTypes: ISelectOption[]
  ) => void;
};

const CompactFilters = ({
  filters,
  selectedFilters,
  onFilterChange,
  categories,
  // Legacy props for backward compatibility
  selectedOrganisations,
  selectedDepartments,
  selectedGroups,
  selectedTemplateTypes,
  selectedRoles,
  onLegacyFilterChange,
  className,
}: CompactFiltersProps) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Handle backward compatibility - convert legacy props to new format
  const isLegacyMode = !filters && selectedOrganisations !== undefined;
  
  const effectiveFilters = useMemo(() => {
    const roleOptions: ISelectOption[] = [
      { label: 'Participant', value: 'Participant', required: false },
      { label: 'Reader', value: 'Reader', required: false },
      { label: 'Owner', value: 'Owner', required: false },
      { label: 'Lead', value: 'Lead', required: false },
    ];

    const legacyFilters: FilterConfig[] = [
      { key: 'organisations', label: 'Organisations', placeholder: 'Select organisations', dataSource: 'api' },
      { key: 'departments', label: 'Departments', placeholder: 'Select departments', dataSource: 'api' },
      { key: 'groups', label: 'Groups', placeholder: 'Select groups', dataSource: 'api' },
      { key: 'templateTypes', label: 'Types', placeholder: 'Select types', dataSource: 'props', data: [] },
      { key: 'roles', label: 'Roles', placeholder: 'Select roles', dataSource: 'props', data: roleOptions },
    ];
    
    return isLegacyMode ? legacyFilters : (filters || []);
  }, [isLegacyMode, filters]);
  
  const effectiveSelectedFilters = useMemo(() => {
    return isLegacyMode ? {
      organisations: selectedOrganisations || [],
      departments: selectedDepartments || [],
      groups: selectedGroups || [],
      templateTypes: selectedTemplateTypes || [],
    } : (selectedFilters || {});
  }, [isLegacyMode, selectedOrganisations, selectedDepartments, selectedGroups, selectedTemplateTypes, selectedFilters]);

  const filterData = useFilterOptions(effectiveFilters);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickInside = Object.values(dropdownRefs.current).some(ref => 
        ref && ref.contains(event.target as Node)
      );
      
      if (!isClickInside) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterToggle = (filterKey: string, option: ISelectOption) => {
    const currentSelection = effectiveSelectedFilters[filterKey] || [];
    const exists = currentSelection.find((o) => o.value === option.value);
    
    let newSelection: ISelectOption[];
    if (exists) {
      newSelection = currentSelection.filter((o) => o.value !== option.value);
    } else {
      newSelection = [...currentSelection, option];
    }

    const newFilters = { ...effectiveSelectedFilters, [filterKey]: newSelection };
    
    if (isLegacyMode) {
      // Convert back to legacy format for backward compatibility
      if (onLegacyFilterChange) {
        onLegacyFilterChange(
          newFilters.organisations || [],
          newFilters.departments || [],
          newFilters.groups || [],
          newFilters.templateTypes || []
        );
      }
    } else {
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
    }
  };

  const handleDateRangeChange = (filterKey: string, end: 'from' | 'to', raw: string) => {
    // Date filters are never used in legacy mode, so there is no legacy branch.
    const current = dateRangeValue(effectiveSelectedFilters[filterKey]);
    const next = { ...current, [end]: raw };
    const newFilters = {
      ...effectiveSelectedFilters,
      [filterKey]: makeDateRangeOption(next.from, next.to),
    };
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const clearAllFilters = () => {
    const clearedFilters: Record<string, ISelectOption[]> = {};
    effectiveFilters.forEach(filter => {
      clearedFilters[filter.key] = [];
    });
    
    if (isLegacyMode) {
      if (onLegacyFilterChange) {
        onLegacyFilterChange([], [], [], []);
      }
    } else {
      if (onFilterChange) {
        onFilterChange(clearedFilters);
      }
    }
  };

  const clearFilter = (filterKey: string) => {
    const newFilters = { ...effectiveSelectedFilters, [filterKey]: [] };
    
    if (isLegacyMode) {
      if (onLegacyFilterChange) {
        onLegacyFilterChange(
          newFilters.organisations || [],
          newFilters.departments || [],
          newFilters.groups || [],
          newFilters.templateTypes || []
        );
      }
    } else {
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
    }
  };

  const isFilterActive = (filterKey: string, value: string): boolean => {
    const selection = effectiveSelectedFilters[filterKey] || [];
    return selection.some((item) => item.value === value);
  };

  const getTotalActiveFilters = () => {
    return Object.values(effectiveSelectedFilters).reduce(
      (total, filterArray) => total + filterArray.length,
      0
    );
  };

  const getSelectedText = (filterKey: string): string => {
    const selection = effectiveSelectedFilters[filterKey] || [];
    const filter = effectiveFilters.find(f => f.key === filterKey);
    const placeholder = filter?.placeholder || 'Select';

    if (selection.length === 0) return placeholder;
    if (filter?.kind === 'dateRange') return selection[0].label;
    return `${selection.length} selected`;
  };

  const hasActiveFilters = getTotalActiveFilters() > 0;

  const toggleDropdown = (filterKey: string) => {
    setOpenDropdown(openDropdown === filterKey ? null : filterKey);
  };

  return (
    <div className={`${styles.container} ${className ?? ""}`}>
      <div className={styles.filterRow}>
        {effectiveFilters.map((filter) => {
          const filterOptions = filterData[filter.key] || [];
          const selectedOptions = effectiveSelectedFilters[filter.key] || [];
          const isDateRangeFilter = filter.kind === 'dateRange';
          const dateRange = dateRangeValue(selectedOptions);

          // Date filters have no options list, so they must bypass this guard.
          if (!isDateRangeFilter && filterOptions.length === 0) return null;

          return (
            <div 
              key={filter.key} 
              className={styles.filterDropdown} 
              ref={(el) => { dropdownRefs.current[filter.key] = el; }}
            >
              <button
                className={`${styles.dropdownButton} ${
                  selectedOptions.length > 0 ? styles.hasSelection : ''
                }`}
                onClick={() => toggleDropdown(filter.key)}
              >
                <span className={styles.dropdownLabel}>{filter.label}</span>
                <span className={styles.dropdownValue}>
                  {getSelectedText(filter.key)}
                </span>
                <span className={styles.dropdownArrow}>
                  {openDropdown === filter.key ? '▲' : '▼'}
                </span>
              </button>

              {openDropdown === filter.key && (
                <div className={styles.dropdownMenu}>
                  <div className={styles.dropdownHeader}>
                    <span className={styles.dropdownTitle}>
                      {filter.placeholder}
                    </span>
                    {selectedOptions.length > 0 && (
                      <button
                        className={styles.clearButton}
                        onClick={() => clearFilter(filter.key)}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  {isDateRangeFilter ? (
                    <div className={styles.dateMenu}>
                      <label className={styles.dateField}>
                        <span className={styles.dateFieldLabel}>From</span>
                        <input
                          type="date"
                          lang="en-GB"
                          className={styles.dateInput}
                          max={dateRange.to || undefined}
                          value={dateRange.from}
                          onChange={(e) => handleDateRangeChange(filter.key, 'from', e.target.value)}
                        />
                      </label>
                      <label className={styles.dateField}>
                        <span className={styles.dateFieldLabel}>To</span>
                        <input
                          type="date"
                          lang="en-GB"
                          className={styles.dateInput}
                          min={dateRange.from || undefined}
                          value={dateRange.to}
                          onChange={(e) => handleDateRangeChange(filter.key, 'to', e.target.value)}
                        />
                      </label>
                    </div>
                  ) : (
                    <div className={styles.optionsList}>
                      {filterOptions.map((option) => {
                        const isActive = isFilterActive(filter.key, option.value);
                        return (
                          <label
                            key={option.value}
                            className={`${styles.optionItem} ${
                              isActive ? styles.checked : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => handleFilterToggle(filter.key, option)}
                              className={styles.checkbox}
                            />
                            <span className={styles.optionLabel}>{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <div className={styles.dropdownFooter}>
                    <button
                      className={styles.doneButton}
                      onClick={() => setOpenDropdown(null)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasActiveFilters && (
        <div className={styles.clearAllContainer}>
          <button className={styles.clearAllButton} onClick={clearAllFilters}>
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(CompactFilters);

